/**
 * Pin / boost collection products from ad query params.
 * Example: /collections/all-new?pin_ids=8572139143351,1234567890
 * Also accepts: ?utm_pin_id=... and ?pin=...
 *
 * On-page pins are reordered instantly (no network).
 * Off-page pins: tiny ?view=pin-ids lookup (~1KB), then one Section
 * Rendering fetch for only the page that contains the product.
 */
(function () {
  if (window.collectionPinProductsLoaded) return;
  window.collectionPinProductsLoaded = true;

  const PARAM_KEYS = ['pin_ids', 'utm_pin_id', 'pin'];

  // Safety: never run pin logic on a normal collection URL.
  const hasPinParam = PARAM_KEYS.some((key) => new URLSearchParams(window.location.search).get(key));
  if (!hasPinParam) return;

  const INJECTED_ATTR = 'data-pin-injected';
  const FETCH_OPTS = { priority: 'low' };

  let cachedPinIds = null;
  let cachedParamKey = PARAM_KEYS[0];
  let abortController = null;
  let productIdsCache = null;
  const sectionPageCache = new Map();

  function parseIds(raw) {
    return String(raw)
      .split(',')
      .map((id) => id.trim())
      .filter((id) => /^\d+$/.test(id));
  }

  function readPinIds() {
    const params = new URLSearchParams(window.location.search);

    for (const key of PARAM_KEYS) {
      const raw = params.get(key);
      if (raw) {
        cachedPinIds = parseIds(raw);
        cachedParamKey = key;
        break;
      }
    }

    return cachedPinIds || [];
  }

  function restorePinParamInUrl(ids) {
    if (!ids.length) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get(cachedParamKey) === ids.join(',')) return;

    url.searchParams.set(cachedParamKey, ids.join(','));
    history.replaceState({}, '', url.toString());
  }

  function getProductList() {
    return (
      document.querySelector('#collection-products product-list') ||
      document.querySelector('product-list.product-list')
    );
  }

  function findCard(root, id) {
    return root.querySelector(`product-card[data-product-id="${id}"]`);
  }

  function movePinnedCardsToFront(list, ids) {
    const fragment = document.createDocumentFragment();

    for (const id of ids) {
      const card = findCard(list, id);
      if (card) fragment.appendChild(card);
    }

    if (fragment.childElementCount > 0) list.prepend(fragment);
  }

  /** Active filters break page math — skip remote fetch (on-page reorder still works). */
  function canLocateOffPage() {
    const params = new URLSearchParams(window.location.search);
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) return false;
    }
    return true;
  }

  function afterCriticalPaint() {
    return new Promise((resolve) => {
      const run = () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => resolve(), { timeout: 3000 });
        } else {
          setTimeout(resolve, 1500);
        }
      };
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    });
  }

  async function loadCollectionProductIds(collectionUrl, signal) {
    if (productIdsCache) return productIdsCache;

    // Tiny alternate template — IDs only, not full product JSON (~1KB).
    const url = `${collectionUrl}?view=pin-ids`;
    const response = await fetch(url, { ...FETCH_OPTS, signal });
    if (!response.ok) return [];

    const text = (await response.text()).trim();
    let ids = [];
    try {
      ids = JSON.parse(text).map(String);
    } catch {
      return [];
    }

    productIdsCache = ids;
    return ids;
  }

  async function fetchSectionPage(page, sectionId, signal) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));

    const key = url.toString();
    if (sectionPageCache.has(key)) return sectionPageCache.get(key);

    const response = await fetch(key, { ...FETCH_OPTS, signal });
    if (!response.ok) return null;

    const document_ = new DOMParser().parseFromString(await response.text(), 'text/html');
    sectionPageCache.set(key, document_);
    return document_;
  }

  async function injectPinnedCardsFromOtherPages(list, ids, signal) {
    const missingIds = ids.filter((id) => !findCard(list, id));
    if (missingIds.length === 0) return false;
    if (!canLocateOffPage()) return false;

    const section = list.closest('.shopify-section');
    const sectionId = section ? section.id.replace('shopify-section-', '') : null;
    if (!sectionId) return false;

    const currentPage = parseInt(list.getAttribute('data-current-page'), 10) || 1;
    const totalPages = parseInt(list.getAttribute('data-total-pages'), 10) || 1;
    const pageSize =
      parseInt(list.getAttribute('data-page-size'), 10) ||
      list.querySelectorAll(':scope > product-card').length ||
      48;
    const collectionUrl = list.getAttribute('data-collection-url') || window.location.pathname;

    if (totalPages < 2) return false;

    const allIds = await loadCollectionProductIds(collectionUrl, signal);
    if (signal.aborted || !allIds.length) return false;

    const pagesNeeded = new Set();
    const idToPage = new Map();

    for (const id of missingIds) {
      const index = allIds.indexOf(id);
      if (index === -1) continue;

      const page = Math.floor(index / pageSize) + 1;
      if (page === currentPage || page < 1 || page > totalPages) continue;

      pagesNeeded.add(page);
      idToPage.set(id, page);
    }

    if (pagesNeeded.size === 0) return false;

    let injected = false;

    for (const page of pagesNeeded) {
      if (signal.aborted) break;

      const document_ = await fetchSectionPage(page, sectionId, signal);
      if (!document_) continue;

      for (const [id, targetPage] of idToPage) {
        if (targetPage !== page) continue;

        const card = findCard(document_, id);
        if (!card) continue;

        const clone = document.importNode(card, true);
        clone.removeAttribute('reveal-on-scroll');
        clone.setAttribute(INJECTED_ATTR, 'true');
        list.prepend(clone);
        injected = true;
      }
    }

    return injected;
  }

  async function applyPinReorder() {
    const ids = readPinIds();
    if (ids.length === 0) return;

    const list = getProductList();
    if (!list) return;

    list.querySelectorAll(`[${INJECTED_ATTR}]`).forEach((card) => card.remove());
    movePinnedCardsToFront(list, ids);
    restorePinParamInUrl(ids);

    // Instant path: every pin is already on this page — zero network.
    if (ids.every((id) => findCard(list, id))) return;

    if (abortController) abortController.abort();
    abortController = new AbortController();
    const signal = abortController.signal;

    await afterCriticalPaint();
    if (signal.aborted) return;

    const injected = await injectPinnedCardsFromOtherPages(list, ids, signal);
    if (injected && !signal.aborted) movePinnedCardsToFront(list, ids);
  }

  applyPinReorder();

  document.documentElement.addEventListener('theme:loading:end', () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(applyPinReorder);
    });
  });
})();
